import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BriefcaseBusinessIcon, FilterIcons } from '../../../../icons/icons';
import { LocaleSwitcher } from '../../../../i18n/components/LocaleSwitcher';
import { JOB_PORTAL_KEYS, NS } from '../../../../i18n/constants/keys';
import { PaginationNavigator } from '../../../../ui/PaginationNavigator';
import { getJobPostList } from '../../../../utils/api_service/JobPosting';
import { RootState } from '../../../../utils/redux_store/store';
import {
  FilterState,
  JobPostingData,
} from '../../../../utils/types/JobOpenings';
import JobPostingFilter from '../JobPostingFilter';
import JobCard from './JobCard';

type ViewMode = 'grid' | 'list';

const JobPostTable = () => {
  const { t } = useTranslation(NS.jobPortal);
  const [filters, setFilters] = useState<FilterState>({
    jobTitle: [],
    location: [],
    workType: [],
    department: [],
  });
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [search, setSearch] = useState<boolean>(true);
  const [jobPostData, setJobPostData] = useState<JobPostingData[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const { companyLogo, companyName } = useSelector(
    (state: RootState) => state.logo,
  );
  const limit = 12;
  const [maxPages, setMaxPages] = useState<number>(1);
  const activeFilterCount = [
    ...filters.jobTitle,
    ...filters.location,
    ...filters.workType,
    ...filters.department,
  ].length;

  const scrollGridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchJobPost = async () => {
      setIsLoading(true);
      try {
        const response = await getJobPostList(
          filters.jobTitle,
          filters.location,
          filters.department,
          filters.workType,
          pageNumber,
          limit,
        );
        const responseData = await response.json();
        setJobPostData(responseData.results);
        setMaxPages(Math.ceil(responseData.total / limit));
      } catch (error) {
        console.error('Error fetching job post:', error);
        setJobPostData([]);
        setMaxPages(1);
      } finally {
        setIsLoading(false);
      }
    };
    fetchJobPost();
  }, [search, pageNumber, filters]);

  useEffect(() => {
    if (scrollGridRef.current) {
      scrollGridRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pageNumber, search, filters]);

  return (
    <div className='min-h-screen w-full bg-slate-100'>
      <header className='border-b border-slate-200 bg-white'>
        <div className='mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8'>
          <div className='grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center'>
            <div className='flex min-w-0 items-center gap-4'>
              <div className='flex h-14 min-w-14 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 shadow-sm'>
                { companyLogo ? (
                  <img
                      src={companyLogo}
                      alt={companyName || 'Company logo'}
                      className='max-h-10 max-w-[160px] object-contain'
                  />
                ) : (
                  <BriefcaseBusinessIcon className='h-6 w-6 text-slate-700' />
                ) }
              </div>
              <div className='min-w-0'>
                <p className='mb-1 text-xs font-bold uppercase tracking-wide text-emerald-700'>
                  Careers
                </p>
                <h1 className='truncate text-3xl font-extrabold text-slate-950'>
                  { t(JOB_PORTAL_KEYS.listing.title) }
                </h1>
                <p className='mt-1 text-sm font-semibold text-slate-500'>
                  { companyName
                    ? `Explore open roles at ${companyName}`
                    : t(JOB_PORTAL_KEYS.listing.subtitle) }
                </p>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-3'>
              <LocaleSwitcher variant='inline' />
              <div className='rounded-lg border border-slate-200 bg-slate-50 px-4 py-3'>
                <div className='text-2xl font-extrabold text-slate-950'>
                  { jobPostData.length }
                </div>
                <div className='text-xs font-bold text-slate-500'>
                  { t(JOB_PORTAL_KEYS.listing.availableJobs) }
                </div>
              </div>
              <div className='rounded-lg border border-slate-200 bg-slate-50 px-4 py-3'>
                <div className='text-2xl font-extrabold text-slate-950'>
                  { maxPages }
                </div>
                <div className='text-xs font-bold text-slate-500'>
                  { t(JOB_PORTAL_KEYS.listing.pages) }
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      { /* Main Content Area with Sidebar Layout */ }
      <div className='mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8'>
        <div className='grid grid-cols-1 gap-5 lg:grid-cols-[300px_minmax(0,1fr)]'>
          { /* Left Sidebar - Filters (Desktop) */ }
          <div className='hidden lg:block'>
            <div className='sticky top-5 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm'>
              <div className='border-b border-slate-200 p-4'>
                <div className='flex items-center gap-3'>
                  <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50'>
                    <FilterIcons className='h-5 w-5 text-emerald-600' />
                  </div>
                  <div>
                    <h2 className='text-base font-extrabold text-slate-900'>
                      { t(JOB_PORTAL_KEYS.listing.filters) }
                    </h2>
                    <p className='text-xs font-semibold text-slate-500'>
                      Narrow down open roles
                    </p>
                  </div>
                </div>
              </div>
              <JobPostingFilter
                  filters={filters}
                  setFilters={setFilters}
                  setSearch={setSearch}
                  search={search}
              />
            </div>
          </div>

          { /* Mobile Filter Toggle */ }
          <div className='lg:hidden'>
            <button
                onClick={() => setShowFilters(!showFilters)}
                className='flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm'
            >
              <div className='flex items-center gap-3'>
                <div className='w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center'>
                  <svg
                      className='w-5 h-5 text-emerald-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z'
                    />
                  </svg>
                </div>
                <div>
                  <span className='block text-sm font-extrabold text-slate-900'>
                    { t(JOB_PORTAL_KEYS.listing.filtersAndSearch) }
                  </span>
                  <span className='block text-xs font-semibold text-slate-500'>
                    { activeFilterCount } active filters
                  </span>
                </div>
              </div>
              <svg
                  className={`w-6 h-6 text-gray-600 transform transition-transform duration-200 ${
                    showFilters ? 'rotate-180' : ''
                  }`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                />
              </svg>
            </button>

            <div
                className={`${
                  showFilters ? 'block' : 'hidden'
                } mt-3 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm`}
            >
              <JobPostingFilter
                  filters={filters}
                  setFilters={setFilters}
                  setSearch={setSearch}
                  search={search}
              />
            </div>
          </div>

          { /* Right Content Area */ }
          <div className='min-w-0'>
            { /* View Controls */ }
            <div className='mb-4 rounded-lg border border-slate-200 bg-white shadow-sm'>
              <div className='flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex items-center gap-4'>
                  <div className='flex items-center gap-2'>
                    <div className='h-2 w-2 rounded-full bg-emerald-500'></div>
                    <p className='text-sm font-semibold text-slate-700'>
                      { t(JOB_PORTAL_KEYS.listing.showing) }{ ' ' }
                      <span className='font-extrabold text-emerald-700'>
                        { jobPostData.length }
                      </span>{ ' ' }
                      { t(JOB_PORTAL_KEYS.listing.jobs) }
                      { maxPages > 1 && (
                        <span className='text-gray-500'>
                          { ' ' }
                          ({ t(JOB_PORTAL_KEYS.listing.page) } { pageNumber } { t(JOB_PORTAL_KEYS.listing.of) } { maxPages })
                        </span>
                      ) }
                    </p>
                  </div>
                  { isLoading && (
                    <div className='flex items-center gap-2 text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full'>
                      <svg
                          className='animate-spin w-4 h-4'
                          fill='none'
                          viewBox='0 0 24 24'
                      >
                        <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'
                        ></circle>
                        <path
                            className='opacity-75'
                            fill='currentColor'
                            d='m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        ></path>
                      </svg>
                      <span className='text-sm font-medium'>{ t(JOB_PORTAL_KEYS.listing.loading) }</span>
                    </div>
                  ) }
                </div>

                { /* View Toggle */ }
                <div className='flex items-center gap-3'>
                  <span className='text-xs font-bold text-slate-600'>{ t(JOB_PORTAL_KEYS.listing.view) }:</span>
                  <div className='flex rounded-lg bg-slate-100 p-1'>
                    <button
                        onClick={() => setViewMode('grid')}
                        className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs font-bold transition ${
                          viewMode === 'grid'
                            ? 'bg-emerald-500 text-white shadow-lg'
                            : 'text-gray-600 hover:text-emerald-600'
                        }`}
                    >
                      <svg
                          className='w-4 h-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z'
                        />
                      </svg>
                      { t(JOB_PORTAL_KEYS.listing.grid) }
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs font-bold transition ${
                          viewMode === 'list'
                            ? 'bg-emerald-500 text-white shadow-lg'
                            : 'text-gray-600 hover:text-emerald-600'
                        }`}
                    >
                      <svg
                          className='w-4 h-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M4 6h16M4 10h16M4 14h16M4 18h16'
                        />
                      </svg>
                      { t(JOB_PORTAL_KEYS.listing.list) }
                    </button>
                  </div>
                </div>
              </div>
              { activeFilterCount > 0 && (
                <div className='border-t border-slate-100 px-4 py-2 text-xs font-semibold text-slate-500'>
                  { activeFilterCount } filter{ activeFilterCount === 1 ? '' : 's' } applied
                </div>
              ) }
            </div>

            { /* Jobs Content */ }
            <div ref={scrollGridRef} className='min-h-[360px]'>
              { isLoading ? (
                <div className='flex items-center justify-center rounded-lg border border-slate-200 bg-white py-16 shadow-sm'>
                  <div className='text-center'>
                    <div className='relative w-20 h-20 mx-auto mb-6'>
                      <div className='absolute inset-0 rounded-full border-4 border-emerald-200'></div>
                      <div className='absolute inset-0 rounded-full border-4 border-transparent border-t-emerald-600 animate-spin'></div>
                    </div>
                    <p className='text-emerald-700 text-xl font-semibold'>
                      { t(JOB_PORTAL_KEYS.listing.findingOpportunities) }
                    </p>
                    <p className='text-gray-600 mt-2'>
                      { t(JOB_PORTAL_KEYS.listing.loadingJobPostings) }
                    </p>
                  </div>
                </div>
              ) : jobPostData.length > 0 ? (
                <div
                    className={`
                  ${
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3'
                      : 'flex flex-col gap-3'
                  }
                `}
                >
                  { jobPostData.map((job, index) => (
                    <div key={job.id || index} className='w-full'>
                      <JobCard jobPostData={job} viewMode={viewMode} />
                    </div>
                  )) }
                </div>
              ) : (
                <div className='flex min-h-[360px] items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-10 text-center shadow-sm'>
                  <div>
                    <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50'>
                      <BriefcaseBusinessIcon className='h-8 w-8 text-emerald-600' />
                    </div>
                    <h3 className='mb-2 text-xl font-extrabold text-slate-900'>
                      { t(JOB_PORTAL_KEYS.listing.noJobsFound) }
                    </h3>
                    <p className='mx-auto mb-5 max-w-md text-sm leading-6 text-slate-600'>
                      { t(JOB_PORTAL_KEYS.listing.noJobsMessage) }
                    </p>
                    <button
                        onClick={() => {
                          setFilters({
                            jobTitle: [],
                            location: [],
                            workType: [],
                            department: [],
                          });
                          setSearch(!search);
                        }}
                        className='inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700'
                    >
                      <svg
                          className='w-5 h-5'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                        />
                      </svg>
                      { t(JOB_PORTAL_KEYS.listing.clearAllFilters) }
                    </button>
                  </div>
                </div>
              ) }
            </div>

            { /* Pagination */ }
            { jobPostData.length > 0 && maxPages > 1 && (
              <div className='mt-4 flex justify-center'>
                <div className='rounded-lg border border-slate-200 bg-white p-2 shadow-sm'>
                  <PaginationNavigator
                      pageNumber={pageNumber}
                      maxPageNumber={maxPages}
                      setPageNumber={setPageNumber}
                  />
                </div>
              </div>
            ) }
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobPostTable;
